import React, { useEffect, useState } from "react";
import Footer from "../../core/common/footer/Footer";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";
import { all_routes } from "../router/all_routes";
import CandidateDetails from "./components/CandidateDetails";
import StepProgress from "./components/StepProgress";
import { useSelector } from "react-redux";
import useApi from "../../core/customhook/useApi";
import SnipperLoader from "../../core/common/loader/SnipperLoader";

const LocationVerification = () => {
  const navigate = useNavigate();

  const reduxDashboardData = useSelector(
    (state: any) => state?.dashboard?.dashboardData,
  );

  const candidateId = reduxDashboardData?.assignmentDetails?.candidate_id;

  const getAddressAPI = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const updateLocationAPI = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}/geo-location`,
    {
      method: "PATCH",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  // ========= live location state ===
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  // ========= User enter address location state ===
  const [addressLatitude, setAddressLatitude] = useState("");
  const [addressLongitude, setAddressLongitude] = useState("");

  // ========= distance calculate  state ===

  const [locationDistance, setLocationDistance] = useState<number | null>(null);
  const [locationMismatch, setLocationMismatch] = useState(false);

  const [locationLoading, setLocationLoading] = useState(false);
  const [mapUrl, setMapUrl] = useState("");
  const [locationAllowed, setLocationAllowed] = useState(false);
  const [locationError, setLocationError] = useState("");
  // const [signature, setSignature] = useState<File | null>(null);
  const [confirmAddress, setConfirmAddress] = useState(true);
  // ---------- Get Api Data State ------------------------
  const [detailData, setDetailData] = useState<any>(null);

  const [originalLatitude, setOriginalLatitude] = useState("");
  const [originalLongitude, setOriginalLongitude] = useState("");

  const getFullAddress = () => {
    if (!detailData) return "";

    return [
      detailData?.address_line1,
      detailData?.city,
      detailData?.state,
      detailData?.pincode,
      detailData?.country,
    ]
      .filter(Boolean)
      .join(", ");
  };

  const GOOGLE_MAPS_API_KEY = "AIzaSyApVnqHJ_X5QDl1nRj9Iqh784n-PPpdOfg";

  const getLatLngFromAddress = async (address: string) => {
    try {
      if (!address) {
        throw new Error("Address is required");
      }

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address,
        )}&key=${GOOGLE_MAPS_API_KEY}`,
      );

      const data = await response.json();

      if (data.status !== "OK" || !data.results?.length) {
        console.error("Google Geocoding Error:", data);

        throw new Error("Unable to find location for the given address.");
      }

      const location = data.results[0].geometry.location;

      return {
        latitude: location.lat,
        longitude: location.lng,
        formattedAddress: data.results[0].formatted_address,
      };
    } catch (error) {
      console.error("Geocoding Error:", error);

      return null;
    }
  };

  const handleGetLocation = async () => {
    const address = getFullAddress();
    if (!address) {
      console.log("Address not available");
      return;
    }
    const location = await getLatLngFromAddress(address);

    if (location) {
      // IMPORTANT
      setAddressLatitude(location.latitude.toString());
      setAddressLongitude(location.longitude.toString());
    }
  };

  useEffect(() => {
    if (!detailData) return;

    handleGetLocation();
  }, [detailData]);

  useEffect(() => {
    if (!candidateId) return;
    const fetchDetails = async () => {
      try {
        const res = await getAddressAPI.callApi();

        if (res?.success) {
          const data = res.data[0];

          setDetailData(data);
          if (data?.address_latitude && data?.address_longitude) {
            setLatitude(data.address_latitude);
            setLongitude(data.address_longitude);

            setOriginalLatitude(data.address_latitude);
            setOriginalLongitude(data.address_longitude);

            setLocationAllowed(true);

            setMapUrl(
              `https://maps.google.com/maps?q=${data.address_latitude},${data.address_longitude}&z=15&output=embed`,
            );
          }
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchDetails();
  }, [candidateId]);

  const calculateDistanceInMeters = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
  ) => {
    const R = 6371000; // Earth radius in metres

    const toRadians = (degree: number) => {
      return (degree * Math.PI) / 180;
    };

    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRadians(lat1)) *
        Math.cos(toRadians(lat2)) *
        Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const compareLocations = (
    currentLat: number,
    currentLng: number,
    addressLat: number,
    addressLng: number,
  ) => {
    const distance = calculateDistanceInMeters(
      currentLat,
      currentLng,
      addressLat,
      addressLng,
    );

    setLocationDistance(distance);

    if (distance > 1000) {
      setLocationMismatch(true);
    } else {
      setLocationMismatch(false);
    }
  };

  useEffect(() => {
    if (
      latitude === "" ||
      longitude === "" ||
      addressLatitude === "" ||
      addressLongitude === ""
    ) {
      return;
    }

    const currentLat = Number(latitude);
    const currentLng = Number(longitude);

    const addressLat = Number(addressLatitude);
    const addressLng = Number(addressLongitude);

    if (
      !Number.isFinite(currentLat) ||
      !Number.isFinite(currentLng) ||
      !Number.isFinite(addressLat) ||
      !Number.isFinite(addressLng)
    ) {
      console.log("Invalid coordinates");
      return;
    }

    compareLocations(currentLat, currentLng, addressLat, addressLng);
  }, [latitude, longitude, addressLatitude, addressLongitude]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      Swal.fire({
        icon: "error",
        title: "Location Not Supported",
        text: "Your browser doesn't support GPS.",
      });

      return;
    }

    setLocationLoading(true);
    setLocationError("");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        // Save LIVE location
        setLatitude(lat.toString());
        setLongitude(lng.toString());

        setLocationAllowed(true);

        setMapUrl(
          `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`,
        );

        setLocationLoading(false);

        // DO NOT compare here.
        // useEffect will compare automatically.
      },

      (error) => {
        console.error("GPS Error:", error);

        setLocationLoading(false);
        setLocationAllowed(false);

        setLocationError(
          "Location access was denied or unavailable. Please enable GPS/location permissions and try again.",
        );

        Swal.fire({
          icon: "warning",
          title: "Location Permission Required",
          text: "Please enable your GPS location to continue.",
        });
      },

      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  };

  const handleNext = async () => {
    if (!confirmAddress) {
      Swal.fire({
        icon: "warning",
        title: "Confirmation Required",
        text: "Please confirm that the above address is true and correct.",
      });
      return;
    }

    try {
      const currentLat = Number(latitude);
      const currentLng = Number(longitude);
      const addressLat = Number(addressLatitude);
      const addressLng = Number(addressLongitude);

      const isValidCoordinate = (value: string, min: number, max: number) =>
        value !== "" &&
        Number.isFinite(Number(value)) &&
        Number(value) >= min &&
        Number(value) <= max;

      // Calculate distance whenever both coordinate pairs exist.
      const hasValidCoordinates =
        isValidCoordinate(latitude, -90, 90) &&
        isValidCoordinate(longitude, -180, 180) &&
        isValidCoordinate(addressLatitude, -90, 90) &&
        isValidCoordinate(addressLongitude, -180, 180);

      let calculatedDistance: number | null = null;

      if (hasValidCoordinates) {
        calculatedDistance = calculateDistanceInMeters(
          currentLat,
          currentLng,
          addressLat,
          addressLng,
        );

        setLocationDistance(calculatedDistance);
        setLocationMismatch(calculatedDistance > 1000);
      }

      // Always call API regardless of the 1 km threshold.
      const response = await updateLocationAPI.callApi({
        address_type: detailData?.address_type,
        latitude: latitude,
        longitude: longitude,
        distance:
          calculatedDistance !== null ? String(calculatedDistance) : null,
      });

      if (response?.success) {
        navigate(all_routes.selfieInfo);
        return;
      }

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: response?.message || "Something went wrong.",
      });
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      });
    }
  };

  // const handleNext = async () => {
  //   if (!latitude || !longitude) {
  //     Swal.fire({
  //       icon: "warning",
  //       title: "Location Required",
  //       text: "Please share your current location before continuing.",
  //     });
  //     return;
  //   }

  //   if (!confirmAddress) {
  //     Swal.fire({
  //       icon: "warning",
  //       title: "Confirmation Required",
  //       text: "Please confirm that the above address is true and correct.",
  //     });
  //     return;
  //   }

  //   try {
  //     // Always send location and distance to API,
  //     // whether inside or outside 1000 meters.
  //     const response = await updateLocationAPI.callApi({
  //       address_type: detailData?.address_type,
  //       latitude: latitude,
  //       longitude: longitude,
  //       distance: String(locationDistance),
  //     });

  //     if (response?.success) {
  //       navigate(all_routes.selfieInfo);
  //       return;
  //     }

  //     Swal.fire({
  //       icon: "error",
  //       title: "Update Failed",
  //       text: response?.message || "Something went wrong.",
  //     });
  //   } catch (error: any) {
  //     Swal.fire({
  //       icon: "error",
  //       title: "Update Failed",
  //       text:
  //         error?.response?.data?.message ||
  //         error?.message ||
  //         "Something went wrong.",
  //     });
  //   }
  // };

  return (
    <div className="page-wrapper">
      <div className="content">
        {getAddressAPI.loading ? (
          <SnipperLoader />
        ) : (
          <>
            {/* Shared step tracker — this page is step 2 of 5, step 1 already done */}
            <StepProgress totalSteps={5} currentStep={2} completedSteps={[1]} />

            <CandidateDetails addressInfo={detailData} />

            <div className="card p-4 overflow-hidden">
              {/* Document header strip */}
              <div className="border-bottom pb-3 mb-4">
                <h5 className="fw-semibold">Address Authentication</h5>
                <p className="text-dark">
                  Verify your current location and sign below to authenticate
                  your address.
                </p>
              </div>

              <div>
                {/* Location block */}
                <div
                  className="d-flex align-items-start gap-2 p-3 mb-3 rounded-3"
                  style={{
                    background: "#FFF8F1",
                    border: "1px solid #F6D5B3",
                    borderLeft: "4px solid #E67E22",
                  }}
                >
                  <i
                    className="ti ti-info-circle mt-1 flex-shrink-0"
                    style={{
                      color: "#D97706",
                      fontSize: "18px",
                    }}
                  ></i>

                  <p
                    className="mb-0 text-dark"
                    style={{ fontSize: "14px", lineHeight: "1.5" }}
                  >
                    Please make sure you're available at the given address. If
                    the distance between the submitted address and your current
                    location is more than{" "}
                    <strong style={{ color: "#D97706" }}>1 kilometers</strong>,
                    it will create a discrepancy at the time of verification.
                  </p>
                </div>

                <div
                  className="card border-0 shadow-sm mb-4"
                  style={{
                    borderRadius: "16px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    className="card-body p-4"
                    style={{
                      background:
                        "linear-gradient(135deg, #fff8f3 0%, #ffffff 55%, #fff4eb 100%)",
                    }}
                  >
                    <div className="row align-items-center">
                      {/* Left Side */}
                      <div className="col-lg-8">
                        <div className="d-flex align-items-center">
                          <div
                            className="me-3 d-flex align-items-center justify-content-center"
                            style={{
                              width: 65,
                              height: 43,
                              borderRadius: "50%",
                              background: "#F26522",
                              color: "#fff",
                            }}
                          >
                            <i className="ti ti-map-pin fs-20"></i>
                          </div>

                          <div>
                            <h4 className="fw-bold mb-2">
                              Detect your location
                            </h4>

                            <p className="text-dark mb-3">
                              Your live location helps us verify your current
                              address during the background verification
                              process. Your location is only accessed after your
                              permission.
                            </p>

                            <div className="d-flex flex-wrap gap-2">
                              <span
                                className="badge rounded-pill"
                                style={{
                                  background: "#FFF1E8",
                                  color: "#F26522",
                                  padding: "8px 14px",
                                }}
                              >
                                <i className="ti ti-shield-check me-1"></i>
                                Secure
                              </span>

                              <span
                                className="badge rounded-pill"
                                style={{
                                  background: "#E8F8EE",
                                  color: "#198754",
                                  padding: "8px 14px",
                                }}
                              >
                                <i className="ti ti-lock me-1"></i>
                                Privacy Protected
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right Side start */}
                      <div className="col-lg-4 text-lg-end mt-4 mt-lg-0">
                        <div className="mb-2">
                          {locationAllowed ? (
                            <small className="text-success fw-semibold">
                              <i className="ti ti-check me-1"></i>
                              Location Verified
                            </small>
                          ) : (
                            <small className="text-dark fw-semibold">
                              Click here to share your location
                            </small>
                          )}
                        </div>
                        <button
                          onClick={getCurrentLocation}
                          disabled={locationLoading}
                          className="btn"
                          style={{
                            background: locationAllowed ? "#198754" : "#F26522",
                            color: "#fff",
                          }}
                        >
                          {locationLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                              Detecting...
                            </>
                          ) : locationAllowed ? (
                            <>
                              <i className="ti ti-refresh me-2"></i>

                              {detailData?.address_latitude
                                ? "Re-detect Location"
                                : "Location Captured"}
                            </>
                          ) : (
                            <>
                              <i className="ti ti-current-location me-2"></i>
                              Share Location
                            </>
                          )}
                        </button>
                      </div>

                      {/* Right Side start */}
                    </div>
                  </div>
                </div>
                {locationError && (
                  <div
                    style={{
                      backgroundColor: "#fdeaea",
                      border: "1px solid #f6cdd0",
                      color: "#c0392b",
                      borderRadius: "8px",
                      padding: "10px 14px",
                      fontSize: "13px",
                      marginBottom: "16px",
                    }}
                  >
                    {locationError}
                  </div>
                )}
                {/* ---- map start from here -------- */}
                <div className="card mt-4">
                  {/* Coordinates */}
                  {latitude && longitude && (
                    <>
                      <div className="row g-3 px-3 py-3">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            <i className="ti ti-map-pin me-2 text-danger"></i>
                            Latitude
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={latitude}
                            readOnly
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            <i className="ti ti-map-pin me-2 text-primary"></i>
                            Longitude
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={longitude}
                            readOnly
                          />
                        </div>
                      </div>

                      <hr style={{ borderColor: "#828386" }} />
                    </>
                  )}

                  {/* Google Map */}
                  <div
                    style={{
                      height: "400px",
                      overflow: "hidden",
                      borderRadius: "12px",
                    }}
                  >
                    {mapUrl ? (
                      <iframe
                        title="Google Map"
                        src={mapUrl}
                        width="100%"
                        height="100%"
                        style={{
                          border: 0,
                          display: "block",
                        }}
                        loading="lazy"
                      />
                    ) : (
                      <div
                        className="d-flex flex-column justify-content-center align-items-center h-100"
                        style={{
                          background: "#fafafa",
                          color: "#adb5bd",
                        }}
                      >
                        <i
                          className="ti ti-map-2"
                          style={{ fontSize: "60px" }}
                        ></i>

                        <h5 className="mt-3">Map Preview</h5>

                        <p className="mb-0 text-muted">
                          Your location map will appear here.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {/* ---- map end from here -------- */}
                <hr style={{ margin: "32px 0", borderColor: "#919297" }} />
                {/* Confirmation block */}
                <h6 className="fw-semibold mb-2" style={{ color: "#2b1a0f" }}>
                  <i
                    className="ti ti-shield-check me-2"
                    style={{ color: "#F26522" }}
                  ></i>
                  Confirm your Address
                </h6>
                <div
                  className="d-flex gap-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => setConfirmAddress(!confirmAddress)}
                >
                  <input
                    type="checkbox"
                    className="form-check-input mt-1"
                    style={{ flexShrink: 0 }}
                    checked={confirmAddress}
                    onChange={(e) => setConfirmAddress(e.target.checked)}
                  />
                  <p className="text-dark mb-0">
                    I confirm that the above address is true and correct to the
                    best of my knowledge. I authorize the use of my location and
                    submitted details for background verification purposes.
                  </p>
                </div>
                {/* <hr style={{ margin: "32px 0", borderColor: "#808186" }} /> */}
                {/* <div className="d-flex align-items-start gap-3 mb-3">
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "10px",
                  backgroundColor: "#FFF4EB",
                  flexShrink: 0,
                }}
              >
                <i
                  className="ti ti-signature"
                  style={{
                    color: "#F26522",
                    fontSize: "22px",
                  }}
                ></i>
              </div>

              <div>
                <h6 className="fw-semibold mb-1" style={{ color: "#2b1a0f" }}>
                  Digital Signature
                </h6>

                <p
                  className="text-muted mb-0"
                  style={{
                    fontSize: "13px",
                    lineHeight: "1.6",
                  }}
                >
                  Draw your signature below to acknowledge and authorize this
                  submission.
                </p>
              </div>
            </div>

            <div>
              <ESignaturePad
                field="signature"
                onChange={handleSignatureChange}
                // existingSignatureUrl={null}
                // error={errors.applicant_sign}
                // isDisabled={saveLoading}
              />
            </div> */}
              </div>

              <hr style={{ margin: "32px 0", borderColor: "#919297" }} />

              {/* Location Mismatch Warning */}
              {locationMismatch && locationDistance !== null && (
                <div
                  className="d-flex align-items-start gap-3 p-3 mb-3 rounded-3"
                  style={{
                    background: "#FFF5F5",
                    border: "1px solid #F5C2C7",
                    borderLeft: "4px solid #DC3545",
                  }}
                >
                  {/* Warning Icon */}
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: "38px",
                      height: "38px",
                      background: "#FDE2E4",
                    }}
                  >
                    <i
                      className="ti ti-alert-triangle"
                      style={{
                        fontSize: "20px",
                        color: "#DC3545",
                      }}
                    ></i>
                  </div>

                  {/* Message */}
                  <div className="flex-grow-1">
                    <h6 className="mb-1 fw-bold text-danger">
                      Location Mismatch
                    </h6>

                    <p
                      className="mb-1 text-dark"
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.5",
                      }}
                    >
                      You are approximately{" "}
                      <strong className="text-danger">
                        {Math.round(locationDistance)} metres
                      </strong>{" "}
                      away from the submitted address.
                    </p>

                    <p
                      className="mb-0 text-secondary"
                      style={{
                        fontSize: "13px",
                        lineHeight: "1.5",
                      }}
                    >
                      Please make sure you are physically present at the address
                      you have provided before proceeding.
                    </p>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-end gap-3">
                <button
                  // onClick={() => setShowCancelModal(true)}
                  onClick={() => navigate(-1)}
                  className="btn btn-outline-primary"
                >
                  Back
                </button>

                <button
                  type="button"
                  onClick={handleNext}
                  className="btn btn-primary"
                  // disabled={locationMismatch}
                >
                  Save & Next
                </button>
              </div>
            </div>
          </>
        )}

        {/* {showCancelModal && (
          <CancelAuthentication
            show={showCancelModal}
            onClose={() => setShowCancelModal(false)}
          />
        )} */}
      </div>
      <Footer />
    </div>
  );
};

export default LocationVerification;
